import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ProjectAccessSelectionState } from "@/types/projects";

type ProjectAccessSelectionGateProps = {
  busy?: boolean;
  onSubmit: (projectIds: string[]) => Promise<void> | void;
  selection: ProjectAccessSelectionState;
};

function formatUpdatedAt(value: string, locale: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function ProjectAccessSelectionGate({
  busy = false,
  onSubmit,
  selection,
}: ProjectAccessSelectionGateProps) {
  const { i18n, t } = useTranslation();
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(
    selection.selectedProjectIds,
  );
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    setSelectedProjectIds(selection.selectedProjectIds);
    setErrorKey(null);
  }, [selection.selectedProjectIds, selection.requiredProjectCount, selection.required]);

  const requiredProjectCount = selection.requiredProjectCount;
  const selectedCount = selectedProjectIds.length;
  const hasRequiredSelectionCount = selectedCount === requiredProjectCount;

  const candidateById = useMemo(
    () => new Set(selection.candidateProjects.map((candidate) => candidate.projectId)),
    [selection.candidateProjects],
  );

  function toggleProject(projectId: string, checked: boolean) {
    if (busy) {
      return;
    }

    if (!candidateById.has(projectId)) {
      return;
    }

    setErrorKey(null);
    setSelectedProjectIds((current) => {
      if (checked) {
        if (current.includes(projectId) || current.length >= requiredProjectCount) {
          return current;
        }

        return [...current, projectId];
      }

      return current.filter((id) => id !== projectId);
    });
  }

  function handleSubmit() {
    if (!hasRequiredSelectionCount) {
      setErrorKey("projects.accessSelection.requiredError");
      return;
    }

    void onSubmit(selectedProjectIds);
  }

  return (
    <Card className="project-shell-panel border-amber-400/45 bg-amber-50/40">
      <CardHeader className="space-y-2">
        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.26em] text-amber-800/80">
          <Lock className="h-3.5 w-3.5" />
          {t("projects.accessSelection.lockedEyebrow")}
        </p>
        <CardTitle className="text-2xl text-amber-900">
          {t("projects.accessSelection.title")}
        </CardTitle>
        <p className="text-sm leading-6 text-amber-900/85">
          {t("projects.accessSelection.body", {
            required: requiredProjectCount,
            tier: t(`common.tiers.${selection.tier}`),
          })}
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-700/20 bg-amber-100/50 px-4 py-3 text-sm text-amber-900/85">
          <span>{t("projects.accessSelection.hint", { required: requiredProjectCount })}</span>
          <span className="font-semibold">
            {t("projects.accessSelection.selectedCount", {
              required: requiredProjectCount,
              selected: selectedCount,
            })}
          </span>
        </div>

        {selection.candidateProjects.length === 0 ? (
          <p className="rounded-xl border border-dashed border-amber-700/25 px-4 py-4 text-sm text-amber-900/75">
            {t("projects.accessSelection.empty")}
          </p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {selection.candidateProjects.map((project) => {
              const checked = selectedProjectIds.includes(project.projectId);
              const disabled = busy || (!checked && selectedCount >= requiredProjectCount);

              return (
                <label
                  className={[
                    "cursor-pointer rounded-xl border px-4 py-3 transition",
                    checked
                      ? "border-amber-700/55 bg-amber-100/80"
                      : "border-amber-700/20 bg-white/75 hover:border-amber-700/45",
                    disabled ? "cursor-not-allowed opacity-60" : "",
                  ].join(" ")}
                  key={project.projectId}
                >
                  <div className="flex items-start gap-3">
                    <input
                      checked={checked}
                      className="mt-1 h-4 w-4 rounded border-amber-700/50 text-amber-700 focus:ring-amber-500"
                      disabled={disabled}
                      type="checkbox"
                      onChange={(event) => toggleProject(project.projectId, event.target.checked)}
                    />
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-amber-950">{project.title}</p>
                      <p className="text-xs text-amber-900/70">{project.genre}</p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-amber-900/65">
                        {formatUpdatedAt(project.updatedAt, i18n.language)}
                      </p>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {errorKey ? (
          <p className="text-sm text-rose-700">
            {t(errorKey, { required: requiredProjectCount })}
          </p>
        ) : null}

        <div className="flex justify-end">
          <Button
            className="min-w-[16rem]"
            disabled={busy || !hasRequiredSelectionCount}
            type="button"
            onClick={handleSubmit}
          >
            {busy
              ? t("projects.accessSelection.submitting")
              : t("projects.accessSelection.submit")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
